import React from 'react';
import Flex from '~/shared/components/Flex';
import { useUtils } from '~/shared/hooks/useUtils';
import { DataContainer, DataHeader, DataText, DotSeparator, DataHeaderCaption } from '../styled';
import { useTranslation } from '~/shared/utils/translation';
import { GIFT_CERTIFICATE } from '~/features/commerce-api/utils';
import { Order } from '~/lib/data-contract/salesforce';
import { Defaults } from '~/features/checkout/models/checkout';

type props = {
    order: Order | undefined;
};

export const ReceiptData = ({ order }: props) => {
    const { convertPriceToLocale } = useUtils();
    const { translate } = useTranslation();

    return (
        <>
            <DataContainer>
                <DataHeader>{translate('checkout.receipt.orderNumber')}</DataHeader>
                <DataText>{order?.orderNo || '-'}</DataText>
            </DataContainer>

            <DataContainer>
                <DataHeader>{translate('forms.fields.form.address')}</DataHeader>
                <DataText>{order?.billingAddress?.fullName ?? '-'}</DataText>
                <DataText>{order?.billingAddress?.address1 ?? '-'}</DataText>
                <DataText>{order?.billingAddress?.address2}</DataText>
                <DataText>{`${order?.billingAddress?.postalCode ?? '-'} ${order?.billingAddress?.city ?? '-'}`}</DataText>
                {order?.billingAddress?.phone !== Defaults.phoneNumber && (
                    <DataText>{order?.billingAddress?.phone ?? '-'}</DataText>
                )}
            </DataContainer>

            {order?.shipments?.map((shipment, i) => (
                <DataContainer key={shipment.shipmentId}>
                    <Flex>
                        <DataHeader>
                            {order.shipments?.length == 1
                                ? translate('checkout.priceSection.shippingAddress')
                                : translate('checkout.splitdelivery.title', {
                                      current: String(i + 1),
                                      total: String(order.shipments?.length),
                                  })}
                        </DataHeader>
                    </Flex>
                    <DataText>{shipment.shippingAddress?.fullName}</DataText>
                    <DataText>{shipment.shippingAddress?.address1}</DataText>
                    <DataText>{shipment.shippingAddress?.address2}</DataText>
                    <DataText>{`${shipment.shippingAddress?.postalCode} ${shipment.shippingAddress?.city}`}</DataText>
                    {!shipment?.c_pickupStoreID &&
                        shipment?.shippingAddress?.phone !== Defaults.phoneNumber && (
                            <DataText>{shipment?.shippingAddress?.phone}</DataText>
                        )}
                </DataContainer>
            ))}

            <DataContainer>
                <Flex>
                    <DataHeader>{translate('checkout.receipt.payment')}</DataHeader>
                    <DotSeparator />
                    <DataHeaderCaption>{convertPriceToLocale(order?.orderTotal)}</DataHeaderCaption>
                </Flex>

                {order?.paymentInstruments?.map((pi) => (
                    <React.Fragment key={pi.paymentInstrumentId}>
                        {pi.paymentMethodId === GIFT_CERTIFICATE && (
                            <>
                                <DataText>
                                    {translate('checkout.giftCertificateForm.cardName')}{' '}
                                    {pi.maskedGiftCertificateCode}
                                </DataText>
                                <DataText></DataText>
                            </>
                        )}
                        {!!pi.paymentCard && (
                            <DataText>
                                {pi.paymentCard?.cardType} {pi.paymentCard?.maskedNumber}
                            </DataText>
                        )}
                    </React.Fragment>
                ))}
            </DataContainer>
        </>
    );
};
